// @ts-nocheck
import { SettingsCard, SettingsRow } from './SettingsPrimitives';
import { useState } from 'react';
import { errorMessage, errorToastOptions } from '@/services/errorMessage';
import { Phone, Loader2, Trash2, Plus, UserPlus } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '@/hooks/usePermissions';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AddIdentityDialog, IdentityStatusBadge, RemoveIdentityButton, blockedReason, canTakeLine, useCarrierIdentities } from './CarrierIdentities';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';

// ─── Section: Numéros ─────────────────────────────────────────────────────────

export function NumbersSection({ d }) {
  const { t } = useTranslation(['settings', 'common']);
  const [ask, confirmDialog] = useConfirmDialog();
  const queryClient = useQueryClient();
  // The membership role is 'tenant_admin' (or super admin), never 'admin':
  // the old check hid Retirer and Résilier from everyone (2026-10-08).
  const { canReleaseNumber: isAdmin } = usePermissions();
  // The line the admin is about to cancel; the confirmation is an in-app
  // dialog, not the browser's confirm() (Kevin, 2026-10-08).
  const [aResilier, setAResilier] = useState(null);
  // The person a mobile line is ordered for: the operator allows 2 lines per
  // verified identity (CarrierIdentities.tsx).
  const { data: identities = [] } = useCarrierIdentities(isAdmin);
  const [identiteChoisie, setIdentiteChoisie] = useState('');
  const [ajoutPersonne, setAjoutPersonne] = useState(false);
  const [selectPersonneOuvert, setSelectPersonneOuvert] = useState(false);
  const identitesLibres = identities.filter(canTakeLine);
  const identite = identitesLibres.find((i) => i.id === identiteChoisie) || identitesLibres[0] || null;

  // Numeros provisionnes pour ce tenant (source de verite SaaS).
  // Refetch apres provision/release via invalidateQueries.
  const { data: tenantData, isLoading: loadingTenant } = useQuery({
    queryKey: ['tenant-numbers'],
    queryFn: async () => {
      const { data } = await api.functions.invoke('listTenantNumbers');
      return data;
    },
    enabled: !!d.callerData,
  });
  const tenantNumbers = tenantData?.numbers || [];

  const releaseMutation = useMutation({
    mutationFn: async ({ id, mode }) => {
      const { data } = await api.functions.invoke('releaseTenantNumber', { id, mode });
      return { mode, paidUntil: data?.paid_until || null };
    },
    onSuccess: ({ paidUntil }) => {
      // A Zadarma line is not cut at once: it ends with the period already
      // paid, and the toast gives that date when the operator returns it.
      toast.success(paidUntil
        ? t('settings:numbers.releasedUntil', { date: new Date(String(paidUntil).replace(' ', 'T')).toLocaleDateString() })
        : t('settings:numbers.released'));
      queryClient.invalidateQueries({ queryKey: ['tenant-numbers'] });
    },
    onError: (err) => {
      const detail = errorMessage(err, t, 'settings:numbers.unknownError');
      toast.error(t('settings:numbers.releaseFailed', { detail }));
    },
  });

  // #48 -- declaration d'un numero personnel comme numero d'emission.
  // ── Obtenir un numéro : une intention, pas un fournisseur ──────────────
  //
  // Il y avait deux boutons nommés d'après le mécanisme : l'un vérifiait un
  // portable, l'autre achetait une ligne fixe, et la seule ligne capable de
  // porter des SMS ne s'obtenait par aucun écran. On demande désormais ce que
  // le numéro doit FAIRE. Le serveur choisit l'opérateur, et aucun nom
  // d'opérateur n'apparaît ici : le client achète une capacité.
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [choixOffre, setChoixOffre] = useState('');
  const [numeroPerso, setNumeroPerso] = useState('');
  const [codeVerif, setCodeVerif] = useState(null);   // { id, phone, code }
  const [enCours, setEnCours] = useState(false);

  const { data: offresData } = useQuery({
    queryKey: ['number-offers'],
    queryFn: async () => {
      const { data } = await api.functions.invoke('listNumberOffers', {});
      return data;
    },
    enabled: ajoutOuvert,
    staleTime: 60000,
  });
  const offres = offresData?.offers || [];
  const peutCommander = !!offresData?.can_order;
  const offresVisibles = offres.filter((o) => !o.requires_admin || peutCommander);
  // An offer marked coming soon is shown, greyed, and never selected: the
  // choice falls back on the first one that can really be ordered.
  const offresCommandables = offresVisibles.filter((o) => !o.coming_soon);
  const offreChoisie = offresCommandables.some((o) => o.id === choixOffre)
    ? choixOffre
    : (offresCommandables[0]?.id ?? '');
  const offre = offres.find((o) => o.id === offreChoisie);

  const numerosPerso = tenantNumbers.filter((n) => n.provider === 'personal');

  // Le prix vient du serveur, qui le demande aux opérateurs. Inconnu, on le
  // dit : on n'affiche jamais un chiffre qu'on n'a pas.
  const prixOffre = (o) => {
    if (!o) return '';
    if (o.monthly_cost_eur === 0) return t('settings:numbers.offerFree');
    if (o.monthly_cost_eur == null) return t('settings:numbers.offerPriceUnknown');
    const prix = t('settings:numbers.offerMonthly', { amount: o.monthly_cost_eur.toFixed(2) });
    return o.commitment_months > 0
      ? `${prix} · ${t('settings:numbers.offerCommitment', { count: o.commitment_months })}`
      : prix;
  };

  // Le solde du compte de téléphonie. Il ne sert à rien de le montrer en
  // permanence : il ne compte qu'au moment d'engager une dépense, et c'est là
  // qu'il évite un clic qui échouera.
  const solde = offresData?.balance_eur;
  const soldeDevise = offresData?.balance_currency || 'EUR';
  const offreBloquee = offre && offre.requires_admin && offre.affordable === false;


  const validerAjout = async () => {
    setEnCours(true);
    try {
      if (offreChoisie === 'own') {
        const { data } = await api.functions.invoke('addNumber', { offer: 'own', phoneNumber: numeroPerso.trim() });
        setCodeVerif({ id: data.id, phone: data.phone_number, code: data.validation_code });
        setNumeroPerso('');
        setAjoutOuvert(false);
      } else {
        const { data } = await api.functions.invoke('addNumber', { offer: offreChoisie, identityId: identite?.id });
        const obtenu = (data.numbers || [])[0];
        // A number Zadarma still hides digits of ('+337xxx90238') is not
        // named: the full number appears once the operator activates it.
        const nomme = obtenu && !/x/i.test(obtenu.phone_number || '');
        toast.success(nomme
          ? t('settings:numbers.offerOrdered', { number: obtenu.phone_number })
          : t('settings:numbers.offerOrderedNoNumber'));
        setAjoutOuvert(false);
      }
      queryClient.invalidateQueries({ queryKey: ['tenant-numbers'] });
      queryClient.invalidateQueries({ queryKey: ['carrier-identities'] });
    } catch (err) {
      toast.error(errorMessage(err, t, 'settings:numbers.unknownError'), errorToastOptions(err));
    } finally {
      setEnCours(false);
    }
  };

  const confirmerVerification = async () => {
    if (!codeVerif) return;
    setEnCours(true);
    try {
      const { data } = await api.functions.invoke('checkPersonalNumber', { id: codeVerif.id });
      if (data.status === 'verified') {
        toast.success(t('settings:numbers.personalVerified', { number: codeVerif.phone }));
        setCodeVerif(null);
        queryClient.invalidateQueries({ queryKey: ['tenant-numbers'] });
      } else {
        toast.error(t('settings:numbers.personalNotYet'));
      }
    } catch (err) {
      toast.error(errorMessage(err, t, 'settings:numbers.unknownError'), errorToastOptions(err));
    } finally {
      setEnCours(false);
    }
  };

  const retirerNumeroPerso = async (id, phone) => {
    if (!(await ask({ title: t('settings:numbers.personalRemoveConfirm', { number: phone }), confirmLabel: t('common:actions.delete') }))) return;
    try {
      await api.functions.invoke('removePersonalNumber', { id });
      if (codeVerif?.id === id) setCodeVerif(null);
      queryClient.invalidateQueries({ queryKey: ['tenant-numbers'] });
    } catch (err) {
      toast.error(errorMessage(err, t, 'settings:numbers.unknownError'), errorToastOptions(err));
    }
  };

  const changerTitulaire = async (id, identityId) => {
    try {
      await api.functions.invoke('setNumberHolder', { id, identityId });
      queryClient.invalidateQueries({ queryKey: ['tenant-numbers'] });
    } catch (err) {
      toast.error(errorMessage(err, t, 'settings:numbers.unknownError'), errorToastOptions(err));
    }
  };

  // One number: its badges, its status, its actions.
  const renderNumber = (num) => {
            const ns = d.callerIdStats?.[num.phone_number];
            const cr = ns && ns.total > 0 ? Math.round((ns.connected / ns.total) * 100) : null;
            // Active = the caller WILL use this number for outgoing calls.
            // Convention: Membership.selectedCallerIds === [] means "all
            // active tenant numbers". So if d.activeCallerIds is empty,
            // every tenant number is active by default — including any
            // number provisioned later.
            const explicitPrefs: string[] = d.activeCallerIds || [];
            const isActive = explicitPrefs.length === 0
              ? true
              : explicitPrefs.includes(num.phone_number);
            const toggle = () => {
              if (num.status !== 'active') return;
              const allActivePhones = tenantNumbers
                .filter((n) => n.status === 'active')
                .map((n) => n.phone_number);
              // If currently empty (= "all active"), materialise the set
              // before toggling so the user's intent is captured.
              const baseline = explicitPrefs.length === 0 ? allActivePhones : explicitPrefs;
              const next = isActive
                ? baseline.filter((p) => p !== num.phone_number)
                : Array.from(new Set([...baseline, num.phone_number]));
              // If the result equals the full active set, collapse back to
              // [] so newly provisioned numbers stay auto-active.
              const sortedNext = [...next].sort();
              const sortedAll = [...allActivePhones].sort();
              const isFullSet = sortedNext.length === sortedAll.length
                && sortedNext.every((v, i) => v === sortedAll[i]);
              d.setActiveCallerIds(isFullSet ? [] : next);
            };
            return (
              <div key={num.id} className="px-5 py-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground vf-font-mono">
                      {/x/i.test(num.phone_number || '') ? t('settings:numbers.numberPending') : num.phone_number}
                    </p>
                    {num.area_code && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vf-surface-2)', color: 'var(--vf-text-3)' }}>
                        {num.area_code}
                      </span>
                    )}
                    {/* Ce que ce numéro sait faire, d'après l'opérateur. Les
                        trois numéros s'annonçaient capables d'envoyer des SMS
                        alors qu'un seul l'était (mesure du 2026-09-30). */}
                    {num.status !== 'active' ? null : (num.capabilities || []).includes('sms') ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vf-green-bg)', color: 'var(--vf-green)' }}>
                        {t('settings:numbers.smsCapable')}
                      </span>
                    ) : (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--vf-surface-2)', color: 'var(--vf-text-3)' }}
                        title={t('settings:numbers.callsOnlyTitle')}
                      >
                        {t('settings:numbers.callsOnly')}
                      </span>
                    )}
                    {num.status === 'suspended' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vf-orange-bg)', color: 'var(--vf-orange)' }}>
                        {t('settings:numbers.suspended')}
                      </span>
                    )}
                    {/* 2026-05-23 — numéros Zadarma : badge fournisseur + statut
                        RÉEL d'activation (pas de statut inventé). */}
                    {num.provider === 'zadarma' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vf-surface-2)', color: 'var(--vf-text-3)' }}>
                        {t('settings:numbers.mobile07')}
                      </span>
                    )}
                    {num.provider === 'zadarma' && num.status === 'provisioning' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vf-yellow-bg)', color: 'var(--vf-yellow)' }}>
                        {t('settings:numbers.activating')}
                      </span>
                    )}
                    {num.provider === 'zadarma' && num.status === 'active' && num.caller_id_verification === 'pending' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vf-yellow-bg)', color: 'var(--vf-yellow)' }} title={t('settings:numbers.verifyingEmissionTitle')}>
                        {t('settings:numbers.verifyingEmission')}
                      </span>
                    )}
                    {num.provider === 'zadarma' && num.caller_id_verification === 'failed' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vf-red-bg)', color: 'var(--vf-red)' }}>
                        {t('settings:numbers.verificationFailed')}
                      </span>
                    )}
                  </div>
                  {num.provider === 'zadarma' && num.status === 'provisioning' && (
                    <p className="text-xs text-muted-foreground mt-0.5">{t('settings:numbers.activatingHint')}</p>
                  )}
                  {num.paid_until && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('settings:numbers.paidUntil', { date: new Date(num.paid_until).toLocaleDateString() })}
                    </p>
                  )}
                  {ns && ns.total > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('settings:units.callsCount', { count: ns.total })}
                      {cr !== null && (
                        <span className="ml-2 font-medium" style={{ color: cr >= 20 ? 'var(--vf-green)' : cr >= 10 ? 'var(--vf-yellow)' : 'var(--vf-red)' }}>
                          {t('settings:numbers.connectRate', { rate: cr })}
                        </span>
                      )}
                    </p>
                  )}
                </div>
                {/* Status first, never clickable; actions are buttons beside it
                    (Kevin, 2026-10-08: clicking the "Actif" pill to switch it
                    was not discoverable). A line not active yet has neither. */}
                {num.status === 'active' && (
                  <span
                    className="px-2.5 py-0.5 rounded-full text-xs font-medium border"
                    style={isActive
                      ? { background: 'var(--vf-accent-bg)', color: 'var(--vf-accent)', borderColor: 'var(--vf-accent)' }
                      : { background: 'var(--vf-surface-2)', color: 'var(--vf-text-3)', borderColor: 'var(--vf-border)' }}
                  >
                    {isActive ? t('common:states.active') : t('common:states.inactive')}
                  </span>
                )}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {num.status === 'active' && (
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={toggle} title={isActive ? t('settings:numbers.toggleOff') : t('settings:numbers.toggleOn')}>
                      {isActive ? t('settings:numbers.deactivate') : t('settings:numbers.activate')}
                    </Button>
                  )}
                  {/* 'Retirer' (detach: the line stays billed) was removed on
                      2026-10-08 (Kevin: it served no purpose). Cancelling is
                      the only way a number leaves the list. */}
                  {/* A number that is not a Zadarma line is attached to a person
                      by hand: the operator did not register it to anyone. */}
                  {isAdmin && num.provider !== 'zadarma' && identities.length > 0 && (
                    <Select
                      value={num.holder_identity_id || 'none'}
                      onValueChange={(v) => void changerTitulaire(num.id, v === 'none' ? null : v)}
                    >
                      <SelectTrigger className="h-8 w-40 text-xs [&>span]:truncate" aria-label={t('settings:numbers.holder')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="w-[var(--radix-select-trigger-width)]">
                        <SelectItem value="none">{t('settings:numbers.noHolder')}</SelectItem>
                        {identities.map((i) => (
                          <SelectItem key={i.id} value={i.id}><span className="block truncate">{i.first_name} {i.last_name}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {/* The server says whether cancelling is possible. */}
                  {isAdmin && num.can_release !== false && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      style={{ color: 'var(--vf-red)', borderColor: 'var(--vf-red)' }}
                      onClick={() => setAResilier(num)}
                      disabled={releaseMutation.isPending}
                      title={t('settings:numbers.releaseTitle')}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      {t('settings:numbers.release')}
                    </Button>
                  )}
                </div>
              </div>
            );
  };

  // Add a number: a modal, opened from the top button or under a person.
  const dialogueAjout = (
    <Dialog open={ajoutOuvert} onOpenChange={(v) => { if (!enCours) setAjoutOuvert(v); }}>
      <DialogContent className="max-w-md" data-testid="choix-offre">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('settings:numbers.addPhoneNumber')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            {offresCommandables.length > 1 && (<>
            <Label className="text-xs">{t('settings:numbers.offerQuestion')}</Label>
            <Select value={offreChoisie} onValueChange={setChoixOffre}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {offresVisibles.map((o) => (
                  <SelectItem key={o.id} value={o.id} disabled={!!o.coming_soon}>
                    {t(`settings:numbers.offer_${o.id}`)}
                    {o.coming_soon ? ` · ${t('settings:numbers.comingSoon')}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </>)}
            {offre ? (
              <p className="text-xs text-muted-foreground mt-1.5">{prixOffre(offre)}</p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1.5">{t('settings:numbers.askAdmin')}</p>
            )}
          </div>

          {/* A mobile line is opened in a person's name, 2 at most each. The
              first person who can take one is chosen; a person who cannot is
              listed, greyed, with the reason. "Créer une personne" stays
              pinned under the list. */}
          {offreChoisie === 'voice_sms' && (
            <div data-testid="choix-titulaire">
              <Label className="text-xs">{t('settings:identities.orderFor')}</Label>
              <p className="text-xs text-muted-foreground mt-0.5">{t('settings:identities.holderHint')}</p>
              {identities.length === 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-9 mt-1 w-full"
                  data-testid="creer-titulaire"
                  onClick={() => setAjoutPersonne(true)}
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                  {t('settings:identities.create')}
                </Button>
              ) : (
              <Select
                value={identite?.id || ''}
                onValueChange={setIdentiteChoisie}
                open={selectPersonneOuvert}
                onOpenChange={setSelectPersonneOuvert}
              >
                <SelectTrigger className="h-9 mt-1 [&>span]:truncate">
                  <SelectValue placeholder={identities.length === 0 ? t('settings:identities.noneYet') : t('settings:identities.noneAvailable')} />
                </SelectTrigger>
                <SelectContent
                  className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]"
                  footer={(
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-accent"
                      style={{ color: 'var(--vf-accent)' }}
                      onClick={() => { setSelectPersonneOuvert(false); setAjoutPersonne(true); }}
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      {t('settings:identities.create')}
                    </button>
                  )}
                >
                  {identities.map((i) => (
                    <SelectItem key={i.id} value={i.id} disabled={!canTakeLine(i)} className="whitespace-normal">
                      <span className="block truncate">
                        {i.first_name} {i.last_name} · {canTakeLine(i)
                          ? t('settings:identities.lines', { used: i.lines_used, max: i.max_lines })
                          : blockedReason(i, t)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
            </div>
          )}

          {/* Le portable de la personne : on a besoin du numéro. */}
          {offreChoisie === 'own' && (
            <div>
              <Label className="text-xs">{t('settings:numbers.numberLabel')}</Label>
              <Input
                className="h-9 font-mono text-xs mt-1"
                value={numeroPerso}
                placeholder="+33612345678"
                onChange={(e) => setNumeroPerso(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && numeroPerso.trim()) void validerAjout(); }}
              />
              <p className="text-xs text-muted-foreground mt-1.5">{t('settings:numbers.ownConsequence')}</p>
            </div>
          )}

          {/* Provision insuffisante : on le dit AVANT le clic, avec le montant
              exact qui manque. La commande échouait sinon sur un message
              d'opérateur illisible, après coup. */}
          {offreBloquee && (
            <p className="text-xs text-red-700" data-testid="solde-insuffisant">
              {t('settings:numbers.balanceTooLow', {
                balance: Number(solde).toFixed(2),
                needed: Number(offre.upfront_cost_eur).toFixed(2),
                currency: soldeDevise,
              })}
            </p>
          )}

          {/* Le solde, visible dès qu'une option payante est choisie. */}
          {offre?.requires_admin && solde != null && !offreBloquee && (
            <p className="text-xs text-muted-foreground">
              {t('settings:numbers.balanceCurrent', { balance: Number(solde).toFixed(2), currency: soldeDevise })}
            </p>
          )}

        </div>
        <DialogFooter>
          <Button size="sm" variant="ghost" className="h-9" onClick={() => setAjoutOuvert(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            size="sm"
            className="h-9"
            disabled={enCours || !offre || offreBloquee || (offreChoisie === 'own' && !numeroPerso.trim()) || (offreChoisie === 'voice_sms' && !identite)}
            onClick={() => void validerAjout()}
          >
            {enCours ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            {offreChoisie === 'own' ? t('settings:numbers.personalStart') : t('settings:numbers.offerOrder')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // A personal number being verified, and the personal numbers.
  const blocPerso = (
    <div className="space-y-3">
      {/* Le code dicté par l'opérateur. Tant qu'il est à l'écran, la personne
          a quelque chose à faire sur son téléphone. */}
      {codeVerif && (
        <div className="rounded-lg border border-border p-4 space-y-3" data-testid="verification-numero-personnel">
          <p className="text-sm text-foreground">
            <Trans
              i18nKey="settings:numbers.personalCallIncoming"
              values={{ number: codeVerif.phone }}
              components={{ b: <strong className="font-mono" /> }}
            />
          </p>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-mono font-semibold tracking-[0.3em] text-foreground">{codeVerif.code}</span>
            <Button size="sm" className="h-9" disabled={enCours} onClick={() => void confirmerVerification()}>
              {enCours ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              {t('settings:numbers.personalTyped')}
            </Button>
            <Button size="sm" variant="ghost" className="h-9" onClick={() => setCodeVerif(null)}>
              {t('common:actions.cancel')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t('settings:numbers.personalCallHint')}</p>
        </div>
      )}

      {numerosPerso.length > 0 && (
        <div className="rounded-lg border border-border divide-y divide-border/60">
          {numerosPerso.map((n) => {
            const verifie = n.caller_id_verification === 'verified';
            return (
              <div key={n.id} className="flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-mono text-foreground">{n.phone_number}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vf-surface-2)', color: 'var(--vf-text-3)' }}>
                    {t('settings:numbers.callsOnly')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {verifie ? (
                    <span className="text-[10px] font-medium px-2 py-1 rounded" style={{ background: 'var(--vf-green-bg)', color: 'var(--vf-green)' }}>
                      {t('settings:numbers.personalUsable')}
                    </span>
                  ) : (
                    <span className="text-[10px] font-medium px-2 py-1 rounded bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
                      {t('settings:numbers.toVerify')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => void retirerNumeroPerso(n.id, n.phone_number)}
                    aria-label={t('settings:numbers.removeAria', { number: n.phone_number })}
                    className="p-1.5 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Numbers grouped by the person they are registered to; the others (not a
  // Zadarma line, or a person not known here) in one card below.
  const lignes = tenantNumbers.filter((n) => n.provider !== 'personal');
  // A Zadarma line belongs to the person it was ordered for; any other
  // number to the person an admin attached it to.
  const numerosDe = (i) => lignes.filter((n) => (n.provider === 'zadarma'
    ? (n.documents_group_id || '0') === i.group_id
    : n.holder_identity_id === i.id));
  const rattache = (n) => identities.some((i) => numerosDe(i).includes(n));
  const autres = lignes.filter((n) => !rattache(n));
  const ouvrirAjout = (identityId = '') => {
    setIdentiteChoisie(identityId);
    setAjoutOuvert(true);
  };
  const rien = !loadingTenant && lignes.length === 0 && identities.length === 0 && numerosPerso.length === 0;

  return (
    <div className="space-y-4">
      {confirmDialog}
      {/* W4: one entry point. « Créer une personne » is a step of adding a
          number (the holder of a mobile line), not a page action; and the
          empty state carries its own button, so this one waits for a first
          number. */}
      {isAdmin && !rien && (
        <div className="flex justify-end gap-2">
          <Button size="sm" className="h-9" onClick={() => ouvrirAjout()}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            {t('settings:numbers.addPhoneNumber')}
          </Button>
        </div>
      )}

      {blocPerso}

      {/* One card per person: who they are, whether the operator verified
          them, their lines, and adding a line in their name. */}
      {isAdmin && identities.map((i) => (
        <SettingsCard key={i.id}>
          <div className="px-5 py-3 flex items-center gap-2 border-b border-border/50" data-testid="titulaire">
            <span className="text-sm font-medium text-foreground">{i.first_name} {i.last_name}</span>
            <IdentityStatusBadge status={i.status} />
            <span className="ml-auto text-xs tabular-nums text-muted-foreground">
              {t('settings:identities.lines', { used: i.lines_used, max: i.max_lines })}
            </span>
            <RemoveIdentityButton identity={i} />
          </div>
          <div className="divide-y divide-border/50">
            {numerosDe(i).map(renderNumber)}
          </div>
          <div className="px-3 py-2">
            {canTakeLine(i) ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded hover:bg-accent/50"
                style={{ color: 'var(--vf-accent)' }}
                onClick={() => ouvrirAjout(i.id)}
              >
                <Plus className="w-3.5 h-3.5" />
                {t('settings:numbers.addPhoneNumber')}
              </button>
            ) : (
              <span className="px-2 text-xs text-muted-foreground">{blockedReason(i, t)}</span>
            )}
          </div>
        </SettingsCard>
      ))}

      {autres.length > 0 && (
        <SettingsCard>
          {/* Lines registered to no known person: numbers verified at
              Twilio (someone's own mobile), or older lines. */}
          {isAdmin && identities.length > 0 && (
            <div className="px-5 py-3 border-b border-border/50 text-sm font-medium text-foreground">
              {t('settings:numbers.otherNumbers')}
            </div>
          )}
          <div className="divide-y divide-border/50">
            {autres.map(renderNumber)}
          </div>
        </SettingsCard>
      )}

      {rien && (
        <div className="bg-card border border-border rounded-xl p-10 text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'var(--vf-accent-bg)', color: 'var(--vf-accent)' }}
          >
            <Phone className="w-7 h-7" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1">{t('settings:numbers.emptyTitle')}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {isAdmin ? t('settings:numbers.emptyHint') : t('settings:numbers.askAdmin')}
          </p>
          {/* The promise « en un clic » needs its click here (audit B2). */}
          {isAdmin && (
            <Button size="sm" className="h-9 mt-4" data-testid="numbers-empty-cta" onClick={() => ouvrirAjout()}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {t('settings:numbers.getNumber')}
            </Button>
          )}
        </div>
      )}

      {dialogueAjout}
      <AddIdentityDialog
        open={ajoutPersonne}
        onOpenChange={setAjoutPersonne}
        onCreated={(id) => setIdentiteChoisie(id)}
      />

      <AlertDialog open={!!aResilier} onOpenChange={(open) => { if (!open) setAResilier(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings:numbers.cancelDialog.title', { number: aResilier?.phone_number })}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
                {aResilier?.provider === 'zadarma' ? (
                  <>
                    <li>{t('settings:numbers.cancelDialog.noRenewal')}</li>
                    <li>{t('settings:numbers.cancelDialog.leavesNow')}</li>
                    <li>
                      {aResilier?.paid_until
                        ? t('settings:numbers.cancelDialog.stopsOn', { date: new Date(aResilier.paid_until).toLocaleDateString() })
                        : t('settings:numbers.cancelDialog.stopsAtEnd')}
                    </li>
                  </>
                ) : (
                  <li>{t('settings:numbers.cancelDialog.givenBack')}</li>
                )}
                <li className="font-medium text-foreground">{t('settings:numbers.cancelDialog.irreversible')}</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (aResilier) releaseMutation.mutate({ id: aResilier.id, mode: 'release' });
                setAResilier(null);
              }}
            >
              {t('settings:numbers.cancelDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {tenantNumbers.length > 1 && (
        <SettingsCard>
          <SettingsRow label={t('settings:numbers.rotationLabel')} last>
            <Select value={d.callerIdRotation} onValueChange={d.setCallerIdRotation}>
              <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">{t('settings:numbers.rotationManual')}</SelectItem>
                <SelectItem value="round-robin">{t('settings:numbers.rotationRoundRobin')}</SelectItem>
                <SelectItem value="random">{t('settings:numbers.rotationRandom')}</SelectItem>
              </SelectContent>
            </Select>
          </SettingsRow>
        </SettingsCard>
      )}

      {/* Bouton ajouter un numero supplementaire */}
      {/* Le troisieme bouton d'ajout a disparu : « Ajouter un numero » en haut
          de page est le seul. Il y en avait trois sur cet ecran, aux noms
          voisins, et aucun ne permettait d'obtenir une ligne capable de SMS. */}
    </div>
  );
}
